/* @flow */

import React from 'react';
import classNames from 'classnames';
import glamorous from 'glamorous';
import useSheet from 'app/jss';

type Props = {
  // whether not the row is active
  active: boolean,
  change?: number,
  // whether or not the user has selected that row to view
  isMarked: boolean,
  // whether or not this row is animating moving up at the moment
  movingUp: boolean,
  rank: number,
  sheet: Object,
};

const defaultProps = {
  change: 0,
};

const UpArrow = glamorous(({ className }) => (
  <svg viewBox="0 0 50 50" width={50} height={50} className={className}>
    <path d="M0 50 L25 0 L50 50 L0 50" fill="currentColor" />
  </svg>
))({
  width: 10,
  height: 10,
  marginRight: 2,
});

const DownArrow = glamorous(UpArrow)({
  transform: 'scaleY(-1)',
});

function LeaderRowPrefix({
  active,
  change = defaultProps.change,
  isMarked,
  movingUp,
  rank,
  sheet: { classes },
}: Props) {
  return (
    <div
      className={classNames(classes.wrapper, {
        active,
        diff: change !== 0,
      })}
    >
      <div className={classes.inner}>
        <div className={classes.rank}>#{rank}</div>
        <div className={classes.trend}>
          {change > 0 && (
            <div
              className={classNames(classes.trendUp, classes.moving, {
                [classes.movingUp]: movingUp && !isMarked,
              })}
            >
              <UpArrow />
              <span>{change}</span>
            </div>
          )}
          {change < 0 && (
            <div className={classNames(classes.trendDown, classes.moving)}>
              <DownArrow />
              <span>{Math.abs(change)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function applyStyles(theme: Theme) {
  return {
    wrapper: {
      alignSelf: 'stretch',
      display: 'flex',
      flexBasis: 'auto',
      flexShrink: 0,
      position: 'relative',
      padding: [0, theme.spacing.xsmall],
      '&.active': {
        '& $trendUp': {
          color: theme.color.white,
        },
      },
      [theme.mediaQuery.tabletPortraitAndUp]: {
        padding: [0, theme.spacing.small],
      },
    },
    inner: {
      display: 'flex',
      flexDirection: 'column',
    },
    rank: {
      alignItems: 'center',
      display: 'flex',
      flexBasis: 'auto',
      flexGrow: 1,
      flexShrink: 1,
      fontSize: theme.fontSize.default,
      justifyContent: 'center',
      width: 40,
    },
    trend: {
      bottom: -8,
      color: theme.color.white,
      display: 'flex',
      fontSize: theme.fontSize.small,
      left: 0,
      justifyContent: 'center',
      position: 'absolute',
      right: 0,
    },
    movingUp: {
      animation: 'trendUpMoving 4s ease-out',
    },
    '@keyframes trendUpMoving': {
      '0%, 25%': {
        color: theme.color.white,
      },
      '100%': {
        color: theme.color.green,
      },
    },
    '@keyframes fadeOut': {
      from: {
        opacity: 1,
      },
      to: {
        opacity: 0,
      },
    },
    moving: {
      animation: 'fadeOut 10s ease-out',
      animationDelay: '12s',
    },
    trendUp: {
      color: theme.color.green,
      display: 'flex',
      alignItems: 'center',
    },
    trendDown: {
      color: theme.color.danger,
      display: 'flex',
      alignItems: 'center',
    },
  };
}

LeaderRowPrefix.defaultProps = defaultProps;

export default useSheet(applyStyles)(LeaderRowPrefix);
